<template>
  <div class="d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20">
    <!--begin::Logo-->
    <router-link :to="{ name: 'Landing' }" class="mb-12 text-center">
      <img alt="Logo" src="@/assets/media/logos/PYBLogo.png" class="h-90px" />
    </router-link>
    <!--end::Logo-->
    <!--begin::Wrapper-->
    <div class="w-lg-600px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto">
      <!--begin::Form-->
      <form
        @submit.prevent="signup"
        class="form w-100 fv-plugins-bootstrap5 fv-plugins-framework"
        novalidate="novalidate"
        id="kt_sign_up_form"
      >
        <!--begin::Heading-->
        <div class="mb-10 text-center">
          <!--begin::Title-->
          <h1 class="text-dark mb-3">Create an Account</h1>
          <!--end::Title-->
          <!--begin::Link-->
          <div class="text-gray-400 fw-bold fs-4">
            Already have an account?
            <a
              class="link-primary fw-bolder text-pink"
              @click="signInHereButton"
            >
              Sign in here
            </a>
          </div>
          <!--end::Link-->
        </div>
        <div class="row fv-row fv-plugins-icon-container">
          <!--begin::Col-->
          <div class="col-xl-6">
            <label class="form-label fw-bolder text-dark fs-6 required"
              >First Name</label
            >
            <input
              class="form-control form-control-lg form-control-solid"
              type="text"
              v-model.trim="$v.firstName.$model"
              autocomplete="off"
            />
            <div
              class="error-text"
              v-if="!$v.firstName.required && $v.firstName.$dirty"
            >
              First name is required
            </div>
          </div>
          <!--end::Col-->
          <!--begin::Col-->
          <div class="col-xl-6">
            <label class="form-label fw-bolder text-dark fs-6 required"
              >Last Name</label
            >
            <input
              class="form-control form-control-lg form-control-solid"
              type="text"
              v-model.trim="$v.lastName.$model"
              autocomplete="off"
            />
            <div
              class="error-text"
              v-if="!$v.lastName.required && $v.lastName.$dirty"
            >
              Last name is required
            </div>
          </div>
          <!--end::Col-->
        </div>
        <!--end::Input group-->
        <!--begin::Input group-->
        <div class="row mb-7 fv-row fv-plugins-icon-container">
          <div class="col-xl-6">
            <label class="form-label fw-bolder text-dark fs-6 required"
              >Email</label
            >
            <input
              class="form-control form-control-lg form-control-solid"
              type="email"
              v-model.trim="$v.email.$model"
              autocomplete="off"
            />
            <div
              class="error-text"
              v-if="!$v.email.required && $v.email.$dirty"
            >
              Email is required
            </div>
            <div class="error-text" v-if="!$v.email.email && $v.email.$dirty">
              Please enter a valid email address
            </div>
          </div>
          <div class="col-xl-6">
            <label class="form-label fw-bolder text-dark fs-6 required"
              >Phone</label
            >
            <i
              data-bs-toggle="tooltip"
              v-b-tooltip.hover
              title="Enter Digits Only"
            >
              <input
                class="form-control form-control-lg form-control-solid"
                type="text"
                v-model="formattedPhoneNumber"
                @focus="unformatPhoneNumber"
                @blur="setPhoneNumber"
                @keypress="filterPhoneNumber($event)"
                autocomplete="off"
                inputmode="numeric"
              />
            </i>
            <div
              class="error-text"
              v-if="!$v.phone.required && $v.phone.$dirty"
            >
              Phone number is required
            </div>
          </div>
        </div>
        <div class="row mb-7 fv-row fv-plugins-icon-container">
          <div
            class="mb-10 fv-row fv-plugins-icon-container"
            data-kt-password-meter="true"
          >
            <!--begin::Wrapper-->
            <div class="mb-1">
              <!--begin::Label-->
              <label class="form-label fw-bolder text-dark fs-6"
                >New Password</label
              >
              <!--end::Label-->
              <!--begin::Input wrapper-->
              <div class="position-relative mb-3">
                <input
                  class="form-control form-control-lg form-control-solid"
                  :type="passwordFieldType"
                  placeholder=""
                  v-model.trim="password"
                  autocomplete="off"
                  required
                  @input="checkPasswordCriteria"
                />
                <span
                  class="
                    btn btn-sm btn-icon
                    position-absolute
                    translate-middle
                    top-50
                    end-0
                    me-n2
                  "
                  data-kt-password-meter-control="visibility"
                >
                  <i
                    :class="eyeIconClass"
                    @click="showPassword = !showPassword"
                    :title="eyeIconTitle"
                  ></i>
                </span>
              </div>
              <!--end::Input wrapper-->
            </div>
            <!--end::Wrapper-->
            <!--begin::Hint-->
            <div class="text-muted" :style="passInstructionStyle">
              Use 8 or more characters with a mix of
              letters(uppercase&amp;lowercase), numbers &amp; symbols.
            </div>
            <!--end::Hint-->
            <div class="fv-plugins-message-container invalid-feedback"></div>
          </div>
          <!--end::Input group-->
          <!--begin::Input group-->
          <div class="fv-row mb-5 fv-plugins-icon-container">
            <label class="form-label fw-bolder text-dark fs-6"
              >Confirm Password</label
            >
            <input
              class="form-control form-control-lg form-control-solid"
              :type="passwordFieldType"
              placeholder=""
              v-model.trim="confirmPassword"
              name="confirm-password"
              autocomplete="off"
              required
              @blur="checkMatch"
            />
            <div class="fv-plugins-message-container invalid-feedback"></div>
            <span style="color: red" v-if="showUnmatchWarning"
              >Password does not match</span
            >
          </div>
        </div>
        <!--end::Input group-->
        <!--begin::Input group-->
        <div class="fv-row mb-10 fv-plugins-icon-container">
          <label
            class="
              form-check form-check-custom form-check-solid form-check-inline
            "
          >
            <input
              class="form-check-input"
              type="checkbox"
              v-model="$v.agree.$model"
            />
            <span class="form-check-label fw-bold text-gray-700 fs-6"
              >I agree to the
              <a
                :href="termsAndConditionsPath"
                class="ms-1 link-primary text-pink"
                target="_blank"
                >Terms and conditions</a
              >.</span
            >
          </label>
          <div class="fv-plugins-message-container invalid-feedback"></div>
        </div>
        <!--end::Input group-->
        <!--begin::Actions-->
        <div class="text-center">
          <button
            type="submit"
            :disabled="!formValid"
            class="btn btn-lg btn-primary"
          >
            <span class="indicator-label">Submit</span>
            <span class="indicator-progress"
              >Please wait...
              <span
                class="spinner-border spinner-border-sm align-middle ms-2"
              ></span
            ></span>
          </button>
        </div>
        <div class="row">
          <div class="col text-center">
            <p>Please check email for confirmation link</p>
          </div>
        </div>
        <!--end::Actions-->
        <div></div>
      </form>
      <!--end::Form-->
    </div>
    <SnackbarNotifier
      :isShow="isShowSnackBar"
      :message="responseMessage"
      :snackType="snackbarType"
      :colour="snackBarColour"
      @eReset="resetSnackBar"
    />
    <!--end::Wrapper-->
  </div>
</template>

<script>
import { required, email } from "vuelidate/lib/validators";
import mixins from "vue-typed-mixins";
import FieldValidationMixin from "@/mixins/FieldValidationMixins";
import SnackbarNotifier from "@/components/SnackbarNotifier.vue";
import SnackbarNotifierMixin from "@/mixins/SnackbarNotifierMixins";
import Appsettings from "@/config/appsettings.json";
import Documents from "@/config/documents.json";

export default mixins(SnackbarNotifierMixin, FieldValidationMixin).extend({
  name: "SignupForm",
  components: {
    SnackbarNotifier,
  },
  data() {
    return {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      kin: "",
      agree: false,
      password: "",
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      showUnmatchWarning: false,
      passInstructionStyle: "",
    };
  },
  validations: {
    firstName: {
      required,
    },
    lastName: {
      required,
    },
    email: {
      required,
      email,
    },
    phone: {
      required,
    },
    agree: {
      checked: (value) => value === true,
    },
  },
  methods: {
    checkPasswordCriteria() {
      this.passInstructionStyle = this.passwordCriteria
        ? ""
        : "color:red !important";
    },
    checkMatch() {
      this.showUnmatchWarning = !this.passwordMatch;
    },
    async signup() {
      this.$v.$touch();
      if (!this.formValid) {
        this.showNotifier({
          message: `Please complete the signup form`,
          snackbarType: "error",
        });
        return;
      }
      let customerInfo = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phone: this.phone,
        password: this.password,
      };
      this.$emit("signUp", customerInfo);
    },
    signInHereButton() {
      this.$emit("signInHereButton");
    },
    setPhoneNumber() {
      this.formatPhoneNumber();
      this.$v.phone.$model = this.formattedPhoneNumber;
    },
  },
  computed: {
    isPasswordValid() {
      return this.passwordCriteria && this.passwordMatch;
    },
    formValid() {
      return (
        !this.$v.$invalid &&
        this.phoneNumberMatchPattern(this.formattedPhoneNumber) &&
        this.isPasswordValid
      );
    },
    eyeIconTitle() {
      return this.showPassword ? "hide password" : "show password";
    },
    passwordFieldType() {
      return this.showPassword ? "text" : "password";
    },
    eyeIconClass() {
      const defaultClass = "bi fs-2";
      return this.showPassword
        ? `${defaultClass} bi-eye`
        : `${defaultClass} bi-eye-slash`;
    },
    passwordCriteria() {
      return /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\`\~\!\@\#\$\%\^\&\*\(\)\_\\\+\=\{\}\[\]\;\:\'\"\,\<\.\>\/\?\-])(?=.{8,})/.test(
        this.password
      );
    },
    passwordMatch() {
      return this.password === this.confirmPassword;
    },
    canConfirmAccount() {
      return this.passwordCriteria && this.passwordMatch;
    },
    termsAndConditionsPath() {
      return `https://${Appsettings.StaticResourcePath}/${Documents.termsAndConditionsPath}`;
    },
  },
});
</script>

<style scoped>
.error-text {
  color: red;
}
</style>
